import type { ReactElement } from 'react';
import { isElement } from 'react-is';
import { Icon } from '@studio/design-system/icon';
import type { CellProps } from './types.js';
import { Tooltip } from '@studio/design-system/tooltip';
import { motion } from '@studio/design-system/motion';

/** @internal Cell label component. */
const CellLabel = ({
  label,
  isMetricsCell
}: {
  label: ReactElement | string;
  isMetricsCell: boolean;
}): ReactElement | null => {
  if (isElement(label)) {
    return label;
  }

  if (isMetricsCell) {
    return null;
  }

  return <div className='mb-4 text-center text-xs text-secondary'>{label}</div>;
};

/** @internal Cell value component. */
const CellValue = ({ value }: { value: ReactElement | string | number }): ReactElement | null => {
  if (isElement(value)) {
    return <div className='w-100 text-nowrap'>{value}</div>;
  }

  return <div className='w-100 text-nowrap text-center'>{value}</div>;
};

/** @internal Metrics cell component */
const MetricsCell = ({
  isMetricsCell,
  message,
  hideIcon
}: {
  isMetricsCell: boolean;
  message?: string | undefined;
  hideIcon?: boolean | undefined;
}): ReactElement | null => {
  if (!isMetricsCell) return null;

  return (
    <>
      {message ? (
        <Tooltip
          zIndex={100}
          trigger={<Icon className='z-100 text-sm hover:cursor-pointer' icon='alert-circle' />}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <div className='rounded-4 bg-[white] px-12 py-6 text-center text-sm font-medium leading-[12px]'>
              {message.split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </motion.div>
        </Tooltip>
      ) : (
        !hideIcon && <Icon className='absolute left-[5%] z-100 text-sm' icon='alert-circle' />
      )}
    </>
  );
};

/** `<Cell>` component. */
export const Cell = ({
  containerClassName,
  label,
  value,
  index,
  message,
  hideIcon,
  ...props
}: CellProps): ReactElement => {
  const isMetricsCell = index === 0;

  return (
    <td {...props}>
      <div className='flex flex-row items-center justify-center gap-8'>
        {value !== null && (
          <>
            <MetricsCell isMetricsCell={isMetricsCell} message={message} hideIcon={hideIcon} />
            <div className={containerClassName}>
              <CellLabel label={label} isMetricsCell={isMetricsCell} />
              <CellValue value={value!} />
            </div>
          </>
        )}
      </div>
    </td>
  );
};
